import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PinOutlinedIcon from '@mui/icons-material/PinOutlined';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import { authFetch } from '../api/api';
import AuthPageLayout, {
  outlinedButtonSx,
  pagePaperSx,
  primaryButtonSx,
  stepperSx,
  textFieldSx,
} from '../components/AuthPageLayout';
import LoggedInGate from '../components/LoggedInGate';
import MessageModal from '../components/MessageModal';
import {
  extractAuthPayload,
  getApiErrorMessage,
  normalizePhone,
  readJsonSafe,
  completeAuthSession,
} from '../utils/authSession';
import { pickLocation } from '../utils/location';

const STEPS = ['login_step_phone', 'login_step_otp', 'login_step_signup'];

export default function Login() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [phone, setPhone] = useState('');
  const [normalizedPhone, setNormalizedPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [fullName, setFullName] = useState('');
  const [locationLabel, setLocationLabel] = useState('');
  const [locationCoords, setLocationCoords] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [messageModal, setMessageModal] = useState({ visible: false, title: '', message: '', variant: 'info' });

  const showMessage = useCallback((title, message, variant = 'error') => {
    setMessageModal({ visible: true, title, message, variant });
  }, []);

  const finishAuth = useCallback(async (json) => {
    const { token, user } = extractAuthPayload(json);
    if (!token) {
      showMessage(t('login_error'), t('login_token_missing'));
      return;
    }
    await completeAuthSession({
      token,
      user: user ?? { name: fullName.trim() || undefined, phone: normalizedPhone, ...locationCoords },
    });
    window.dispatchEvent(new Event('profile-updated'));
    navigate('/profile', { replace: true });
  }, [fullName, locationCoords, navigate, normalizedPhone, showMessage, t]);

  const handleSendOtp = async () => {
    const formattedPhone = normalizePhone(phone);
    if (formattedPhone.length < 10) {
      showMessage(t('login_error'), t('login_phone_invalid'));
      return;
    }
    setLoading(true);
    try {
      const response = await authFetch('/users/auth/send-otp', {
        method: 'POST',
        body: JSON.stringify({ phone: formattedPhone }),
      });
      if (!response.ok) {
        showMessage(t('login_error'), await getApiErrorMessage(response, t('login_request_failed')));
        return;
      }
      setNormalizedPhone(formattedPhone);
      setStep(1);
      showMessage(t('login_otp_sent'), t('login_otp_sent_message'), 'success');
    } catch {
      showMessage(t('login_error'), t('login_request_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.trim().length < 4) {
      showMessage(t('login_error'), t('login_otp_invalid'));
      return;
    }
    setLoading(true);
    try {
      const response = await authFetch('/users/auth/verify-otp', {
        method: 'POST',
        body: JSON.stringify({ phone: normalizedPhone, otp: otp.trim() }),
      });
      const json = await readJsonSafe(response);
      if (!response.ok) {
        showMessage(t('login_error'), json.message ?? t('login_otp_wrong'));
        return;
      }
      const payload = extractAuthPayload(json);
      if (payload.token) {
        await finishAuth(json);
        return;
      }
      if (payload.registered === true) {
        showMessage(t('login_error'), t('register_already_has_account'));
        navigate('/signin');
        return;
      }
      setStep(2);
    } catch {
      showMessage(t('login_error'), t('login_request_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async () => {
    if (!fullName.trim()) {
      showMessage(t('login_error'), t('login_name_required'));
      return;
    }
    if (!locationCoords) {
      showMessage(t('login_error'), t('login_location_required'));
      return;
    }
    setLoading(true);
    try {
      const response = await authFetch('/users/auth/signup', {
        method: 'POST',
        body: JSON.stringify({
          name: fullName.trim(),
          phone: normalizedPhone,
          latitude: locationCoords.latitude,
          longitude: locationCoords.longitude,
        }),
      });
      const json = await readJsonSafe(response);
      if (!response.ok) {
        showMessage(t('login_error'), json.message ?? t('login_request_failed'));
        return;
      }
      await finishAuth(json);
    } catch {
      showMessage(t('login_error'), t('login_request_failed'));
    } finally {
      setLoading(false);
    }
  };

  const pickUserLocation = async () => {
    setLoadingLocation(true);
    try {
      const coords = await pickLocation();
      setLocationCoords(coords);
      setLocationLabel(coords.address);
    } catch {
      showMessage(t('login_error'), t('login_location_failed'));
    } finally {
      setLoadingLocation(false);
    }
  };

  return (
    <AuthPageLayout title={t('Register')} subtitle={t('register_subtitle')}>
      <LoggedInGate>
        <Paper {...pagePaperSx}>
          <Box sx={{ bgcolor: alpha('#2a938f', 0.04), borderBottom: 1, borderColor: 'divider' }}>
            <Stepper activeStep={step} alternativeLabel sx={stepperSx}>
              {STEPS.map((label) => (
                <Step key={label}>
                  <StepLabel>{t(label)}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          <Box sx={{ p: { xs: 3, sm: 4 } }}>
            {step === 0 && (
              <Stack spacing={2.5}>
                <TextField
                  fullWidth
                  label={t('login_phone')}
                  placeholder={t('login_phone_placeholder')}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  type="tel"
                  sx={textFieldSx}
                  InputProps={{
                    startAdornment: <PhoneOutlinedIcon sx={{ mr: 1, color: 'primary.main' }} />,
                  }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleSendOtp}
                  disabled={loading}
                  sx={primaryButtonSx}
                >
                  {loading ? '...' : t('login_send_otp')}
                </Button>
              </Stack>
            )}

            {step === 1 && (
              <Stack spacing={2.5}>
                <Box
                  sx={{
                    py: 1.25,
                    px: 2,
                    borderRadius: 3,
                    textAlign: 'center',
                    bgcolor: alpha('#2a938f', 0.07),
                    border: 1,
                    borderColor: alpha('#2a938f', 0.14),
                  }}
                >
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    {t('login_phone')}
                  </Typography>
                  <Typography fontWeight={800} color="primary.main" sx={{ mt: 0.25 }}>
                    {normalizedPhone}
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  label={t('login_otp')}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  sx={textFieldSx}
                  inputProps={{ maxLength: 6, style: { textAlign: 'center', letterSpacing: 6, fontSize: 20 } }}
                  InputProps={{
                    startAdornment: <PinOutlinedIcon sx={{ mr: 1, color: 'primary.main' }} />,
                  }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleVerifyOtp}
                  disabled={loading}
                  sx={primaryButtonSx}
                >
                  {loading ? '...' : t('login_verify_otp')}
                </Button>
              </Stack>
            )}

            {step === 2 && (
              <Stack spacing={2.5}>
                <TextField
                  fullWidth
                  label={t('login_full_name')}
                  placeholder={t('login_full_name_placeholder')}
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  sx={textFieldSx}
                  InputProps={{
                    startAdornment: <PersonOutlinedIcon sx={{ mr: 1, color: 'primary.main' }} />,
                  }}
                />
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<MyLocationIcon />}
                  onClick={pickUserLocation}
                  disabled={loadingLocation}
                  sx={{
                    ...outlinedButtonSx,
                    justifyContent: 'flex-start',
                    textAlign: 'start',
                    bgcolor: locationLabel ? alpha('#2a938f', 0.06) : 'background.paper',
                  }}
                >
                  {locationLabel || t('login_location_button')}
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleSignup}
                  disabled={loading}
                  sx={primaryButtonSx}
                >
                  {loading ? '...' : t('login_signup_submit')}
                </Button>
              </Stack>
            )}
          </Box>
        </Paper>

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {t('signin_subtitle')}{' '}
            <Link
              component="button"
              variant="body2"
              fontWeight={800}
              color="primary"
              onClick={() => navigate('/signin')}
              sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              {t('Login')}
            </Link>
          </Typography>
        </Box>
      </LoggedInGate>

      <MessageModal {...messageModal} onClose={() => setMessageModal((p) => ({ ...p, visible: false }))} />
    </AuthPageLayout>
  );
}
